import { Flex, Form, Skeleton } from 'antd'
import { T, VStack } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'

import { Logo, LogoColor } from 'src/components/svgs/Logo'
import { EmailTemplateTypeButtonTextMap } from 'src/constants'
import { useRecordEmailPreviewDataQuery } from 'src/fetch/emailPreview'
import { DateFormats, formatDateInTimeZone } from 'src/utils/date'

import { EmailTemplateType } from '../../../types/graphql'
import { useMyOrg } from '../../layouts/AppStateContextLayout/utils'

import { EmailTemplateFormValues } from './types'

type Props = {
  buttonText?: string
  isExpirationNotification?: boolean
  recordId?: number
}
export const EmailPreview = ({ buttonText, isExpirationNotification, recordId }: Props) => {
  const form = Form.useFormInstance()
  Form.useWatch([], form)
  const { content, signature, type, subject } = form.getFieldsValue() as EmailTemplateFormValues

  return (
    <VStack align="stretch" gap={3} padding="12px 0 0 24px" className="border-l border-neutral-200">
      <T.H3>Preview</T.H3>
      <T.BodyLarge>Subject: {subject}</T.BodyLarge>
      <EmailPreviewContent
        buttonText={buttonText}
        content={content}
        isExpirationNotification={!!isExpirationNotification}
        recordId={recordId}
        signature={signature}
        isSignatureHidden={false}
        type={type}
      />
    </VStack>
  )
}

type EmailPreviewContentProps = {
  buttonText?: string
  content: string
  isExpirationNotification?: boolean
  recordId?: number
  type: EmailTemplateType
} & (
  | { isSignatureHidden: true; signature?: never }
  | { isSignatureHidden: false; signature: string }
)
// Previews the email generated by api/src/emails/EmailTemplateEmail.tsx
export const EmailPreviewContent = ({
  buttonText,
  content,
  isExpirationNotification,
  recordId,
  signature,
  type,
  isSignatureHidden = false,
}: EmailPreviewContentProps) => {
  const logoUrl = useMyOrg()?.logoUrl
  const callToAction = buttonText || EmailTemplateTypeButtonTextMap[type]
  const timeZone = useMyOrg()?.timeZone

  const { data: recordData, isLoading: isLoadingRecordData } =
    useRecordEmailPreviewDataQuery(recordId)
  const expirationDate = recordData?.recordById?.expirationDate
  const recordName = recordData?.recordById?.formattedName

  const getTextClassName = (val?: string) => (val ? 'text-neutral-950' : 'text-neutral-500')

  if (isLoadingRecordData) {
    return <Skeleton active />
  }

  return (
    <section className="flex flex-col items-start justify-center gap-6 rounded-sm bg-neutral-100 p-8">
      <div className="flex w-full flex-col gap-6 rounded-lg border border-neutral-200 bg-white p-8">
        <img
          alt="Organization Logo"
          src={logoUrl}
          width="200"
          className="max-h-[100px] max-w-[200px] object-contain"
        />
        <T.Body className={twMerge('whitespace-pre-wrap', getTextClassName(content))}>
          {content ? content.trim() : 'Email content goes here'}
        </T.Body>
        {!!recordName?.trim() && <PreviewItem label="Record" value={recordName} />}
        {isExpirationNotification && !!expirationDate && (
          <PreviewItem
            label="Expiration Date"
            value={formatDateInTimeZone(expirationDate, timeZone, DateFormats.MonthNameDateNoTime)}
          />
        )}
        {callToAction && (
          <div className="text-md my-2 w-full cursor-pointer rounded-lg bg-neutral-950 p-3 text-center text-white">
            {callToAction}
          </div>
        )}
        {!isSignatureHidden && (
          <T.Body className={twMerge('whitespace-pre-wrap', getTextClassName(signature))}>
            {signature ? signature.trim() : 'Signature goes here'}
          </T.Body>
        )}
      </div>
      <VStack align="center" gap={2}>
        <Logo color={LogoColor.Black} showText={false} width={33} height={24} />
        <T.BodySmall className="text-center text-neutral-600">
          GovWell Technologies Inc.
          <br />
          228 Park Ave S PMB 368134, New York, NY 10003-1502
        </T.BodySmall>
      </VStack>
    </section>
  )
}

type PreviewItemProps = {
  label: string
  value: string
}
const PreviewItem = ({ label, value }: PreviewItemProps) => {
  return (
    <Flex vertical>
      <T.Body className="text-neutral-700">{label}</T.Body>
      <T.Body>{value}</T.Body>
    </Flex>
  )
}
